import axios from "axios";
import { isRenderApiUrl, RENDER_API_URL } from "@/lib/site";

const isBrowser = typeof window !== "undefined";
const LOCAL_BACKEND = "http://localhost:8015";
const isProdBuild = process.env.NODE_ENV === "production";

const rawConfigured = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "").trim();
// Never ship a localhost API origin in production builds (common .env mistake).
const configuredBackend =
  isProdBuild && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(rawConfigured)
    ? ""
    : rawConfigured;
const usesRemoteRender = isRenderApiUrl(configuredBackend);

// Production → same origin (Netlify Functions). Local dev → localhost backend.
const BACKEND_URL =
  configuredBackend ||
  (isBrowser ? window.location.origin : isProdBuild ? "https://oorilabel.com" : LOCAL_BACKEND);

// Product images: always prefer the storefront origin in the browser so media/static
// resolve on oorilabel.com even if API points elsewhere.
const ASSET_ORIGIN =
  process.env.REACT_APP_ASSET_URL?.replace(/\/$/, "") ||
  (isBrowser
    ? window.location.origin
    : usesRemoteRender
      ? "https://oorilabel.com"
      : BACKEND_URL);

// Render free/starter tier cold starts can take 30–60s on the first request.
const API_TIMEOUT = usesRemoteRender || isRenderApiUrl(BACKEND_URL) ? 90000 : 12000;

export const API = `${BACKEND_URL}/api`;
export const ASSET = (path) =>
  path?.startsWith("http") ? path : `${ASSET_ORIGIN}${path}`;

export const api = axios.create({ baseURL: API, timeout: API_TIMEOUT });

export const adminApi = axios.create({ baseURL: API, timeout: API_TIMEOUT + 15000 });

const attachToken = (config) => {
  const token = localStorage.getItem("oori_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(attachToken);
adminApi.interceptors.request.use(attachToken);

export const authApi = {
  register: (data) => api.post("/auth/register", data),
  verifyOtp: (data) => api.post("/auth/verify-otp", data),
  resendOtp: (data) => api.post("/auth/resend-otp", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  updateProfile: (data) => api.patch("/auth/me", data),
  changePassword: (data) => api.post("/auth/change-password", data),
};

export const wishlistApi = {
  get: () => api.get("/wishlist"),
  save: (items) => api.put("/wishlist", { items }),
};

export const adminEndpoints = {
  dashboard: () => adminApi.get("/admin/dashboard"),
  products: () => adminApi.get("/admin/products"),
  createProduct: (data) => adminApi.post("/admin/products", data),
  updateProduct: (id, data) => adminApi.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => adminApi.delete(`/admin/products/${id}`),
  deleteAllProducts: () => adminApi.post("/admin/products/delete-all"),
  updateStock: (id, stock_count) => adminApi.patch(`/admin/products/${id}/stock`, { stock_count }),
  updateListing: (id, listing_status, stock_count) =>
    adminApi.patch(`/admin/products/${id}/listing`, { listing_status, stock_count }),
  markSold: (id) => adminApi.post(`/admin/products/${id}/mark-sold`),
  delist: (id) => adminApi.post(`/admin/products/${id}/delist`),
  relist: (id, stock_count) => adminApi.post(`/admin/products/${id}/relist`, { stock_count }),
  syncCatalog: () => adminApi.post("/admin/products/sync"),
  bulkUpdateSizes: (sizes) => adminApi.post("/admin/products/bulk-sizes", sizes ? { sizes } : {}),
  uploadImage: (category, file) => {
    const form = new FormData();
    form.append("category", category);
    form.append("file", file);
    return adminApi.post("/admin/upload", form);
  },
  users: () => adminApi.get("/admin/users"),
  blockUser: (id, is_blocked) => adminApi.patch(`/admin/users/${id}/block`, { is_blocked }),
  deleteUser: (id) => adminApi.delete(`/admin/users/${id}`),
  orders: () => adminApi.get("/admin/orders"),
  updateOrder: (id, status) => adminApi.patch(`/admin/orders/${id}`, { status }),
  shipOrder: (id) => adminApi.post(`/admin/orders/${id}/ship`),
  attachAwb: (id, awb, extras = {}) =>
    adminApi.post(`/admin/orders/${id}/awb`, { awb, ...extras }),
  delhiveryStatus: () => adminApi.get("/admin/delhivery/status"),
  createDelhiveryWarehouse: (data) => adminApi.post("/admin/delhivery/warehouse", data),
  newsletter: () => adminApi.get("/admin/newsletter"),
  offers: () => adminApi.get("/admin/offers"),
  updateOffers: (data) => adminApi.put("/admin/offers", data),
  coupons: () => adminApi.get("/admin/coupons"),
  createCoupon: (data) => adminApi.post("/admin/coupons", data),
  updateCoupon: (id, data) => adminApi.patch(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => adminApi.delete(`/admin/coupons/${id}`),
};

export const orderTracking = (id) => api.get(`/orders/${id}/tracking`);

export const CATEGORIES = [
  { id: "ethnic", name: "Ethnic Wear" },
  { id: "coords", name: "Co-ord Sets" },
  { id: "dresses", name: "Dresses" },
  { id: "tops", name: "Tops" },
];

export const ORDER_STATUSES = ["confirmed", "processing", "shipped", "delivered", "cancelled"];

export const LISTING_STATUSES = [
  { id: "active", label: "Active" },
  { id: "sold", label: "Sold out" },
  { id: "delisted", label: "Delisted" },
];

export { RENDER_API_URL };
